/* eslint-disable camelcase */
import 'animate.css';

import { Grid } from '@mui/material';
import { Location } from '@nx-smartmonkey/shared/domain';
import { TimeWindow } from '@nx-smartmonkey/shared/interfaces';
import { LatLng, MarkerDomainProps, RoutalPalette, RoutalTooltip, VSpacer } from '@nx-smartmonkey/ui';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { validatePhoneNumber } from '@nx-smartmonkey/shared/helpers';
import { useIntl } from 'react-intl';
import DegradedBg from '../../assets/img/onboarding/backgrounds/background-degraded-green.png';
import Back from '../../assets/img/onboarding/icons/back.svg';
import BulletPoint from '../../assets/img/onboarding/icons/bullet_point_empty.svg';
import routalStar from '../../assets/img/routal-star.svg';
import LogoRoutal from '../../assets/svg/LogoRoutal';
import { Vehicle } from '../../domain/vehicle/Vehicle';
import { getRootLocation } from '../../helpers/CustomRouter';
import { useRetrieveOrganization } from '../../hooks/organization/useRetrieveOrganization';
import { useRetrieveActiveProject } from '../../hooks/project/useRetrieveActiveProject';
import { useRetrieveSessionCountry } from '../../hooks/session/useRetrieveSessionCountry';
import { useCreateVehicles } from '../../hooks/vehicle/useCreateVehicles';
import Button from '../../react-components/Button';
import { Map } from '../../react-components/Map';
import Text from '../../react-components/Text';
import eventsPanel from '../../services/events';
import { OnboardingContainer, OnboardingContent } from './Onboarding.styled';
import { OnboardingTopBar } from './OnboardingTopBar';
import { CompanyLocation } from './steps/CompanyLocation';
import { CompanyName } from './steps/CompanyName';
import { Constraints } from './steps/Constraints';
import { Finish } from './steps/Finish';
import { Goal } from './steps/Goal';
import { Section, SectionTypes } from './steps/Section';
import { Start } from './steps/Start';
import { StepsTopBar } from './steps/StepsTopBar';
import { VehiclesTimeWindow } from './steps/VehiclesTimeWindow';
import { CreateVehicles } from './steps/create_vehicles/CreateVehicles';
import { NumVehicles } from './steps/num_of_vehicles/NumVehicles';
import { VehiclesLocation } from './steps/vehicles_location/VehiclesLocation';
import { LocationInputsProps } from './steps/vehicles_location/VehiclesLocationInputs';

const availableSteps = [
  `start`,
  `companyName`,
  `companyLocation`,
  `numVehicles`,
  `goal`,
  `section`,
  `constraints`,
  `vehiclesTimeWindow`,
  `vehiclesLocation`,
  `createVehicles`,
  `finish`,
] as const;

type AvailableSteps = (typeof availableSteps)[number];

export const OnboardingComponent = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: organization } = useRetrieveOrganization();
  const { countryCode } = useRetrieveSessionCountry();
  const { success: createVehiclesSuccess, isLoading, run: createVehicles } = useCreateVehicles();
  const [step, setStep] = useState<AvailableSteps | undefined>(undefined);
  const [organizationLocation, setOrganizationLocation] = useState<Location | undefined>(organization?.location);
  const [section, setSection] = useState<SectionTypes | undefined>(undefined);
  const [otherSection, setOtherSection] = useState<string>(``);
  const [vehiclesTimeWindow, setVehiclesTimeWindow] = useState<TimeWindow>(Vehicle.getDefaultTimeWindow());
  const [vehiclesLocation, setVehiclesLocation] = useState<LocationInputsProps>({
    start: organization?.location,
    end: organization?.location,
  });
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);

  useEffect(() => {
    if (createVehiclesSuccess) {
      onChangeStep(10);
      eventsPanel.onboardingPageView({
        page: `createVehicles`,
        data: { numOfVehicles: vehicles.length },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createVehiclesSuccess]);

  useEffect(() => {
    const urlStepParam = searchParams.get(`step`);

    // check if urlStepParam is a number
    const urlStep = urlStepParam ?? ``;
    if (!availableSteps.includes(urlStep as AvailableSteps)) {
      navigate(`${getRootLocation().pathname}?step=${availableSteps[0]}`);
      onChangeStep(0);
    } else if (urlStep !== step) {
      navigate(`${getRootLocation().pathname}?step=${urlStep}`);
      setStep(urlStep as AvailableSteps);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (organization?.location?.isValid()) {
      setOrganizationLocation(organization.location);
      setVehiclesLocation({
        start: organization.location,
        end: organization.location,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  const onChangeStep = (newStepPosition: number) => {
    navigate(`${getRootLocation().pathname}?step=${availableSteps[newStepPosition]}`);
  };

  const isCreateVehiclesButtonEnabled = () =>
    vehicles.every((vehicle) => vehicle.label !== `` && (!vehicle.phone || validatePhoneNumber(vehicle.phone)));

  const getStepPosition = () => (step ? availableSteps.indexOf(step) : 0);

  const getStepContent = () => {
    let leftElement: React.ReactElement | null = null;
    let rightElement: React.ReactElement | null = null;
    const backButton: React.ReactElement | null = (
      <div
        style={{
          textAlign: `end`,
          padding: `10px 20px`,
          position: `absolute`,
          left: 0,
          bottom: 0,
          zIndex: 1,
        }}
      >
        <Grid
          container
          direction="row"
          onClick={() => {
            onChangeStep(getStepPosition() - 1);
          }}
        >
          <Grid item marginRight={`4px`}>
            <img src={Back} alt="" draggable="false" style={{ width: `18px`, cursor: `pointer` }} />
          </Grid>

          <Grid item display={`flex`} style={{ marginTop: `1px` }}>
            <Text intlId="company.onboarding.back" variant="button" style={{ cursor: `pointer` }} />
          </Grid>
        </Grid>
      </div>
    );
    let nextButton: React.ReactElement | null = null;
    switch (step) {
      case availableSteps[0]:
        return (
          <>
            <OnboardingTopBar color={RoutalPalette.neutral00} />
            <Start
              onNextStep={() => {
                onChangeStep(1);
              }}
            />
          </>
        );
      case availableSteps[1]:
        leftElement = (
          <Grid container justifyContent="center" alignItems="center">
            <CompanyName
              onNextStep={() => {
                onChangeStep(2);
              }}
            />
          </Grid>
        );
        rightElement = (
          <Grid
            container
            item
            alignItems="center"
            justifyContent="center"
            style={{ background: `url(${DegradedBg}) no-repeat`, backgroundSize: `cover`, width: `100%` }}
          >
            <Grid
              item
              container
              justifyContent="center"
              style={{
                position: `relative`,
                marginTop: `-100px`, //TODO poner nueva imagen
                // backgroundImage: `url(${CompanyNameBg})`,
                // backgroundRepeat: `no-repeat`,
                // backgroundPosition: `center`,
                // backgroundSize: `contain`,
              }}
              className={`animate__animated animate__fadeIn animate__faster`}
            >
              <Grid
                item
                container
                direction="column"
                alignItems="center"
                justifyContent="center"
                style={{ position: `absolute`, bottom: `-100px`, width: `630px` }}
              >
                <Grid item>
                  <Text
                    variant="h5"
                    isBold
                    intlId="company.onboarding.name.right_claim.title"
                    style={{ color: `white` }}
                  />
                </Grid>

                <VSpacer medium />

                <Grid item>
                  <Text
                    variant="body-large"
                    intlId="company.onboarding.name.right_claim.description"
                    style={{ textAlign: `center`, color: `white` }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        );
        break;
      case availableSteps[2]: {
        if (!organization || !activeProject) {
          return null;
        }
        leftElement = (
          <Grid container justifyContent="center" alignItems="center">
            <CompanyLocation
              activeProject={activeProject}
              organization={organization}
              organizationLocation={organizationLocation}
              onChange={setOrganizationLocation}
              onNextStep={() => {
                onChangeStep(3);
              }}
            />
          </Grid>
        );
        rightElement = (
          <div style={{ background: RoutalPalette.primary40, width: `100%`, height: `100%` }}>
            <Map
              key="map"
              height={`100%`}
              center={
                organizationLocation?.isValid()
                  ? (organizationLocation!.toCoordinate().getValue() as LatLng)
                  : undefined
              }
              zoomLevel={10}
              language={(organization.country_code ?? countryCode).toLowerCase()}
              markers={
                organizationLocation?.isValid()
                  ? [
                      {
                        id: organization.id,
                        lat: organizationLocation!.lat!,
                        lng: organizationLocation!.lng!,
                        // selected: true,
                        color: RoutalPalette.primary40,
                        kind: `routal_star`,
                      },
                    ]
                  : []
              }
            />
          </div>
        );
        break;
      }
      case availableSteps[3]:
        leftElement = (
          <Grid container justifyContent="center" alignItems="center">
            <NumVehicles
              onNextStep={() => {
                onChangeStep(4);
              }}
            />
          </Grid>
        );
        rightElement = (
          <Grid
            container
            item
            alignItems="center"
            justifyContent="center"
            style={{ background: `url(${DegradedBg}) no-repeat`, backgroundSize: `cover`, width: `100%` }}
          >
            <Grid
              item
              container
              justifyContent="center"
              style={{
                position: `relative`,
                marginTop: `-100px`, //TODO poner nueva imagen
                // backgroundImage: `url(${NumVehiclesBg})`,
                // backgroundRepeat: `no-repeat`,
                // backgroundPosition: `center`,
                // backgroundSize: `contain`,
              }}
              className={`animate__animated animate__fadeIn animate__faster`}
            >
              <Grid
                item
                container
                direction="column"
                alignItems="center"
                justifyContent="center"
                style={{ position: `absolute`, bottom: `-100px`, width: `630px` }}
              >
                <Grid item>
                  <Text
                    variant="h5"
                    isBold
                    intlId="company.onboarding.num_of_vehicles.right_claim.title"
                    style={{ color: `white` }}
                  />
                </Grid>

                <VSpacer medium />

                <Grid item>
                  <Text
                    variant="body-large"
                    intlId="company.onboarding.num_of_vehicles.right_claim.description"
                    style={{ textAlign: `center`, color: `white` }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        );
        break;
      case availableSteps[4]:
        leftElement = (
          <Grid container justifyContent="center" alignItems="center">
            <Goal
              onNextStep={() => {
                onChangeStep(5);
              }}
            />
          </Grid>
        );
        rightElement = (
          <Grid
            container
            item
            alignItems="center"
            justifyContent="center"
            style={{ background: `url(${DegradedBg}) no-repeat`, backgroundSize: `cover`, width: `100%` }}
          >
            <Grid
              item
              container
              justifyContent="center"
              style={{
                width: `100%`,
                position: `relative`,
                marginTop: `-120px`, //TODO poner nueva imagen
                // backgroundImage: `url(${GoalBg})`,
                // backgroundRepeat: `no-repeat`,
                // backgroundPosition: `center`,
                // backgroundSize: `contain`,
              }}
              className={`animate__animated animate__fadeIn animate__faster`}
            >
              <Grid
                item
                container
                direction="column"
                alignItems="center"
                justifyContent="center"
                style={{ position: `absolute`, bottom: `-100px`, width: `630px` }}
              >
                <Grid item>
                  <Text
                    variant="h5"
                    isBold
                    intlId="company.onboarding.goal.right_claim.title"
                    style={{ color: `white` }}
                  />
                </Grid>

                <VSpacer medium />

                <Grid item>
                  <Text
                    variant="body-large"
                    intlId="company.onboarding.goal.right_claim.description"
                    style={{ textAlign: `center`, color: `white` }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        );
        break;
      case availableSteps[5]:
        leftElement = (
          <Grid container justifyContent="center" alignItems="center">
            <Section
              section={section}
              setSection={setSection}
              otherSection={otherSection}
              setOtherSection={setOtherSection}
              onNextStep={() => {
                onChangeStep(6);
              }}
            />
          </Grid>
        );
        rightElement = (
          <Grid
            container
            item
            alignItems="center"
            justifyContent="center"
            style={{ background: `url(${DegradedBg}) no-repeat`, backgroundSize: `cover`, width: `100%` }}
          >
            {section !== undefined && section !== `other` ? (
              <Grid
                item
                container
                justifyContent="center"
                style={{
                  position: `relative`,
                  marginTop: `-100px`,
                  // backgroundImage: `url(${getImageBySection(section)})`,
                  // backgroundRepeat: `no-repeat`,
                  // backgroundPosition: `center`,
                  // backgroundSize: `contain`,
                }}
                className={`animate__animated animate__fadeIn animate__faster`}
              >
                <Grid
                  item
                  container
                  direction="column"
                  alignItems="center"
                  justifyContent="center"
                  style={{
                    position: `absolute`,
                    bottom: section === `retail` ? `-115px` : `-140px`,
                    width: `100%`,
                    maxWidth: `620px`,
                  }}
                >
                  <Grid container direction="column" wrap="nowrap">
                    {Array.from({ length: 3 }).map((_, index) => (
                      <Grid
                        key={index}
                        item
                        container
                        wrap="nowrap"
                        direction="row"
                        alignItems="flex-start"
                        style={{
                          color: `white`,
                          marginBottom: index < 2 ? `24px` : `0px`,
                        }}
                      >
                        <img src={BulletPoint} alt="" draggable="false" width="24px" />

                        <Text
                          variant="h5"
                          style={{ color: `white`, marginLeft: `8px` }}
                          intlId={`company.onboarding.section.option.${section}.info.${index + 1}`}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            ) : null}
          </Grid>
        );
        break;
      case availableSteps[6]:
        if (!activeProject) {
          return null;
        }
        leftElement = (
          <Grid container justifyContent="center" alignItems="center">
            <Constraints
              project={activeProject}
              onNextStep={() => {
                onChangeStep(7);
              }}
            />
          </Grid>
        );
        rightElement = (
          <Grid
            container
            item
            alignItems="center"
            justifyContent="center"
            style={{ background: `url(${DegradedBg}) no-repeat`, backgroundSize: `cover`, width: `100%` }}
          >
            <Grid
              item
              container
              justifyContent="center"
              style={{
                position: `relative`,
                marginTop: `-100px`, //TODO poner nueva imagen
                // backgroundImage: `url(${ConstraintsBg})`,
                // backgroundRepeat: `no-repeat`,
                // backgroundPosition: `center`,
                // backgroundSize: `contain`,
              }}
              className={`animate__animated animate__fadeIn animate__faster`}
            >
              <Grid
                item
                container
                direction="column"
                alignItems="center"
                justifyContent="center"
                style={{ position: `absolute`, bottom: `-100px`, width: `630px` }}
              >
                <Grid item>
                  <Text
                    variant="h5"
                    isBold
                    intlId="company.onboarding.constraints.right_claim.title"
                    style={{ color: `white` }}
                  />
                </Grid>

                <VSpacer medium />

                <Grid item>
                  <Text
                    variant="body-large"
                    intlId="company.onboarding.constraints.right_claim.description"
                    style={{ textAlign: `center`, color: `white` }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        );
        break;
      case availableSteps[7]:
        leftElement = (
          <Grid container justifyContent="center" alignItems="center">
            <VehiclesTimeWindow
              vehiclesTimeWindow={vehiclesTimeWindow}
              onChange={setVehiclesTimeWindow}
              onNextStep={() => {
                onChangeStep(8);
              }}
            />
          </Grid>
        );
        rightElement = (
          <Grid
            container
            item
            alignItems="center"
            justifyContent="center"
            style={{ background: `url(${DegradedBg}) no-repeat`, backgroundSize: `cover`, width: `100%` }}
          >
            <Grid
              item
              container
              justifyContent="center"
              style={{
                position: `relative`,
                marginTop: `-100px`, //TODO poner nueva imagen
                // backgroundImage: `url(${VehiclesTimeWindowsBg})`,
                // backgroundRepeat: `no-repeat`,
                // backgroundPosition: `center`,
                // backgroundSize: `contain`,
              }}
              className={`animate__animated animate__fadeIn animate__faster`}
            >
              <Grid
                item
                container
                direction="column"
                alignItems="center"
                justifyContent="center"
                style={{ position: `absolute`, bottom: `-100px`, width: `630px` }}
              >
                <Grid item>
                  <Text
                    variant="h5"
                    isBold
                    intlId="company.onboarding.vehicles.time_window.right_claim.title"
                    style={{ color: `white` }}
                  />
                </Grid>

                <VSpacer medium />

                <Grid item>
                  <Text
                    variant="body-large"
                    intlId="company.onboarding.vehicles.time_window.right_claim.description"
                    style={{ textAlign: `center`, color: `white` }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        );
        break;
      case availableSteps[8]:
        leftElement = (
          <Grid container justifyContent="center" alignItems="center">
            <VehiclesLocation
              vehiclesLocation={vehiclesLocation}
              onChange={setVehiclesLocation}
              onNextStep={() => {
                onChangeStep(9);
              }}
            />
          </Grid>
        );
        rightElement = (
          <div style={{ background: RoutalPalette.grey8, width: `100%`, height: `100%` }}>
            <Map
              key="map"
              height={`100%`}
              center={
                vehiclesLocation?.start?.isValid()
                  ? (vehiclesLocation.start.toCoordinate().getValue() as LatLng)
                  : undefined
              }
              zoomLevel={10}
              language={(organization?.country_code ?? countryCode).toLowerCase()}
              markers={
                vehiclesLocation?.start?.isValid()
                  ? [
                      {
                        id: `vehicles-location`,
                        lat: vehiclesLocation.start.lat!,
                        lng: vehiclesLocation.start.lng!,
                        color: RoutalPalette.primary40,
                        kind: `routal_star`,
                      },
                    ]
                  : []
              }
            />
          </div>
        );

        break;
      case availableSteps[9]: {
        if (!organization || !activeProject || !vehiclesLocation) {
          return null;
        }
        nextButton = (
          <div
            style={{
              textAlign: `end`,
              padding: `10px 20px`,
              position: `absolute`,
              right: 0,
              bottom: 0,
              zIndex: 1,
            }}
          >
            <Grid item container direction="row" wrap="nowrap" alignItems="center" justifyContent="center">
              <RoutalTooltip
                title={intl.formatMessage({ id: `company.onboarding.vehicles.create.next.tooltip` })}
                visible={!(isCreateVehiclesButtonEnabled() && !isLoading)}
              >
                <Button
                  disabled={!isCreateVehiclesButtonEnabled() || isLoading}
                  intlId={isLoading ? `loading` : `company.onboarding.vehicles.create.next`}
                  style={{
                    width: `200px`,
                    height: `32px`,
                  }}
                  onClick={() => {
                    createVehicles({ vehicles });
                  }}
                />
              </RoutalTooltip>
            </Grid>
          </div>
        );

        leftElement = (
          <Grid container justifyContent="flex-start" alignItems="center" style={{ height: `100%` }}>
            <CreateVehicles
              project={activeProject}
              organization={organization}
              vehiclesTimeWindow={vehiclesTimeWindow}
              vehiclesLocation={vehiclesLocation}
              vehicles={vehicles}
              constraints={{
                volume: activeProject.view.constraints.volume,
                weight: activeProject.view.constraints.weight,
              }}
              onChange={setVehicles}
            />
          </Grid>
        );
        rightElement = (
          <div style={{ background: RoutalPalette.success, width: `100%`, height: `100%` }}>
            <Map
              key="map"
              height={`100%`}
              zoomLevel={10}
              language={(organization.country_code ?? countryCode).toLowerCase()}
              markers={
                vehicles
                  .filter((v) => v.default_start_location?.isValid())
                  .map((v) => ({
                    id: v.id,
                    lat: v.default_start_location!.lat!,
                    lng: v.default_start_location!.lng!,
                    color: v.color?.value,
                    kind: `car`,
                  })) as MarkerDomainProps[]
              }
            />
          </div>
        );
        break;
      }
      case availableSteps[10]:
        leftElement = <Finish />;
        break;
      default:
        return null;
    }

    let log_out_color = RoutalPalette.text;
    if (step === availableSteps[10]) log_out_color = RoutalPalette.neutral00;

    return (
      <>
        <OnboardingTopBar color={log_out_color} />

        <Grid
          container
          direction="row"
          wrap="nowrap"
          style={{ height: `100%`, width: `100%`, position: `relative` }}
          className="animate__animated animate__fadeIn animate__faster"
        >
          {step !== availableSteps[10] ? (
            <StepsTopBar step={availableSteps.indexOf(step)} totalSteps={availableSteps.length - 1} />
          ) : (
            <Grid
              item
              style={{
                display: `flex`,
                position: `absolute`,
                zIndex: `1`,
                top: `38px`,
                left: `38px`,
              }}
            >
              <LogoRoutal color={RoutalPalette.neutral00} width="177px" height="33px" />
              <img src={routalStar} style={{ width: `16px`, height: `16px` }} alt="" />
            </Grid>
          )}

          <Grid item container direction="column" wrap="nowrap" style={{ position: `relative`, height: `100%` }}>
            <OnboardingContent container direction="column" justifyContent="center" alignItems="center" wrap="nowrap">
              {leftElement}
            </OnboardingContent>

            {backButton}

            {nextButton}
          </Grid>

          <Grid item container>
            {rightElement}
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <OnboardingContainer container direction="column" wrap="nowrap" alignItems="center">
      {getStepContent()}
    </OnboardingContainer>
  );
};
